import React, { useEffect, useState } from 'react';
import type { FC } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styles from './SupportAnswersPage.module.scss';

import { useGetSupportChatsUserQuery } from '../../entities/support/api/supportApi';
import { statusLabels } from '../../entities/support/model/constants';
import { formatDate, formatDecisionDays } from '../../entities/support/lib/format';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { TableFooter } from '../../components/ui/TableFooter/TableFooter';
import { formatRangeLabel } from '../../utils/format';
import { DataTable, tableStyles } from '../../components/ui/DataTable/DataTable';
import { SearchField } from '../../components/ui/SearchField/SearchField';
import { toStatus } from '../../entities/support/model/mappers';
import { BackButton } from '../../components/ui/BackButton/BackButton';

export const SupportAnswersPage: FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const setParams = (next: Record<string, string>): void => {
        const params = new URLSearchParams(searchParams);

        Object.entries(next).forEach(([key, value]) => {
            if (value) params.set(key, value);
            else params.delete(key);
        });

        setSearchParams(params, { replace: true });
    };

    const pageNum = Number(searchParams.get('page_num') ?? 1);
    const pageSize = Number(searchParams.get('page_size') ?? 10);
    const query = searchParams.get('query') ?? '';

    const [search, setSearch] = useState(query);

    useEffect(() => {
        setSearch(query);
    }, [query]);

    useEffect(() => {
        if (search === query) return;

        const timer = setTimeout(() => setParams({ query: search, page_num: '1' }), 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const { data, isLoading, isFetching } = useGetSupportChatsUserQuery({
        page_num: pageNum,
        page_size: pageSize,
        query,
    });

    const requests = data?.data ?? [];
    const totalPages = data?.pages ?? 1;
    const total = data?.count ?? 0;

    const setPage = (next: number): void => {
        const safe = Math.min(Math.max(1, next), totalPages);
        if (safe === pageNum) return;
        setParams({ page_num: String(safe) });
    };

    const setPageSize = (next: number): void =>
        setParams({ page_size: String(next), page_num: '1' });

    return (
        <div className={styles.page}>
            <div className={styles.head}>
                <BackButton />
                <PageHeader
                    title="Ответы на обращения"
                    meta={data ? [<>Всего <b>{total}</b></>] : undefined}
                    className={styles.header}
                />
            </div>

            <div className={styles.panel}>
                <SearchField
                    value={search}
                    placeholder="Найти"
                    onChange={setSearch}
                />

                <DataTable
                    static
                    loading={isFetching}
                    isLoading={isLoading}
                    loadingText="Загружаем обращения…"
                    scrollKey={searchParams.toString()}
                    footer={
                        total > 0 ? (
                            <TableFooter
                                page={pageNum}
                                totalPages={totalPages}
                                rangeLabel={formatRangeLabel(pageNum, pageSize, total)}
                                onPageChange={setPage}
                            />
                        ) : undefined
                    }
                    empty={
                        requests.length === 0 ? (
                            <div className={tableStyles.emptyState}>
                                <b>Обращений пока нет</b>
                            </div>
                        ) : undefined
                    }
                >
                    <thead>
                        <tr>
                            <th>Номер заявки</th>
                            <th>Статус</th>
                            <th>Проект</th>
                            <th>Создано</th>
                            <th>Срок</th>
                        </tr>
                    </thead>

                    <tbody>
                        {requests.map((request) => {
                            const status = toStatus(request.status);

                            return (
                                <tr key={request.id}>
                                    <td>
                                        <Link className={styles.link} to={`/support-answers/${request.id}`}>
                                            {request.id}
                                        </Link>
                                    </td>
                                    <td>
                                        <span className={styles.status} data-status={status}>
                                            {statusLabels[status]}
                                        </span>
                                    </td>
                                    <td className={styles.muted}>{request.project ?? 'Не указан'}</td>
                                    <td className={styles.muted}>{formatDate(request.created_at)}</td>
                                    <td className={styles.muted}>
                                        {formatDecisionDays(request.created_at, request.decision_days)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </DataTable>
            </div>
        </div>
    );
};